import { Box, Checkbox, Stack, Tooltip } from "@mui/material";
import { useCallback, useMemo } from "react";
import type { ListVirtualizedSelectContextValue } from "./context";

interface ISelectableRowProps<DataGetId, Data extends DataGetId> {
  index: number;
  data: Data;
  context: ListVirtualizedSelectContextValue<DataGetId, Data>;
}

export const selectableItemContent = <DataGetId, Data extends DataGetId>(
  index: number,
  data: Data,
  context: ListVirtualizedSelectContextValue<DataGetId, Data>
) => <SelectableRow index={index} data={data} context={context} />;

export const SelectableRow = <DataGetId, Data extends DataGetId>({
  index,
  data,
  context,
}: ISelectableRowProps<DataGetId, Data>) => {
  const { itemContent, getId, selectedItems, handleSelect, disabledItems } = context;

  const isSelected = useMemo(
    () =>
      !!selectedItems.find(
        (selectedItem, selectedItemIndex) => getId(selectedItem, selectedItemIndex) === getId(data, index)
      ),
    [selectedItems, data, index, getId]
  );

  const disableItem = useMemo(() => {
    if (!disabledItems) {
      return;
    }

    return disabledItems.find(
      (disabledItem, disabledItemIndex) => getId(disabledItem, disabledItemIndex) === getId(data, index)
    );
  }, [disabledItems, data, index, getId]);

  const handleChange = useCallback(() => {
    handleSelect(data, index);
  }, [data, index, handleSelect]);

  return (
    <Tooltip title={disableItem?.tooltip} disableHoverListener={!disableItem?.tooltip}>
      <Stack
        direction="row"
        spacing={2}
        {...(Boolean(disableItem) && { sx: (theme) => ({ color: theme.palette.gray.dark }) })}
        sx={{
          flex: "1",
          alignItems: "center",
        }}
      >
        <Checkbox
          disabled={Boolean(disableItem)}
          checked={isSelected}
          onChange={handleChange}
          sx={(theme) => ({ padding: theme.spacing(1) })}
        />
        <Box
          sx={{
            flex: "auto",
          }}
        >
          {itemContent(index, data, context)}
        </Box>
      </Stack>
    </Tooltip>
  );
};
